import { Descendant } from "slate";
import { ManualState, NormalizedChapter } from "../types";

/**
 * Helper to check if a node is a heading of a specific level
 */
const isHeading = (node: Descendant, level: 1 | 2 | 3 | 4): boolean => {
  if (!("type" in node)) return false;
  const expectedType = `h${level}`;
  return (
    node.type === expectedType &&
    Array.isArray(node.children) &&
    node.children.length > 0 &&
    "text" in node.children[0]
  );
};

/**
 * Helper to extract text from a node
 */
const getNodeText = (node: Descendant): string => {
  if ("text" in node) return node.text;
  if (
    "children" in node &&
    Array.isArray(node.children) &&
    node.children.length > 0
  ) {
    const firstChild = node.children[0];
    if ("text" in firstChild) return firstChild.text;
  }
  return "";
};

/**
 * Extract chapter title from content (finds first H1)
 */
export const extractChapterTitle = (content: Descendant[]): string => {
  for (const node of content) {
    if (isHeading(node, 1)) {
      return getNodeText(node);
    }
  }
  return "Untitled Chapter";
};

/**
 * Section structure for nested headings
 */
export interface Section {
  id: string;
  title: string;
  level: 2 | 3 | 4;
  number: string; // e.g., "1.1", "1.1.2", "1.1.2.3"
  children: Section[];
}

/**
 * Extract H2/H3/H4 sections from chapter content with hierarchical numbering
 */
export const extractSections = (
  content: Descendant[],
  chapterNumber: number,
): Section[] => {
  const sections: Section[] = [];
  const stack: { section: Section; counters: number[] }[] = [];
  const counters = [0, 0, 0]; // For H2, H3, H4

  content.forEach((node, index) => {
    if (isHeading(node, 2) || isHeading(node, 3) || isHeading(node, 4)) {
      const level =
        (node as any).type === "h2" ? 2 : (node as any).type === "h3" ? 3 : 4;
      const levelIndex = level - 2; // 0 for H2, 1 for H3, 2 for H4

      // Increment counter for this level
      counters[levelIndex]++;

      // Reset counters for deeper levels
      for (let i = levelIndex + 1; i < counters.length; i++) {
        counters[i] = 0;
      }

      // Build number string
      let numberParts = [chapterNumber];
      for (let i = 0; i <= levelIndex; i++) {
        if (counters[i] > 0) {
          numberParts.push(counters[i]);
        }
      }
      const number = numberParts.join(".");

      const section: Section = {
        id: `section-${index}`,
        title: getNodeText(node),
        level,
        number,
        children: [],
      };

      // Pop stack until we find appropriate parent
      while (
        stack.length > 0 &&
        stack[stack.length - 1].section.level >= level
      ) {
        stack.pop();
      }

      // Add to parent or root
      if (stack.length === 0) {
        sections.push(section);
      } else {
        stack[stack.length - 1].section.children.push(section);
      }

      stack.push({ section, counters: [...counters] });
    }
  });

  return sections;
};

/**
 * Convert old flat Slate format → normalized structure
 * This function parses the Slate content and extracts chapters based on H1 headings
 */
export const deserializeFromSlate = (
  slateContent: Descendant[],
): Omit<
  ManualState,
  | "selectedChapterId"
  | "selectedSubChapterId"
  | "hasUnsavedChanges"
  | "isLoading"
> => {
  const chapters: Record<string, NormalizedChapter> = {};
  const chapterOrder: string[] = [];

  let currentChapterId: string | null = null;
  let currentChapterContent: Descendant[] = [];
  let order = 0;

  slateContent.forEach((node, index) => {
    if (isHeading(node, 1)) {
      // Save previous chapter if exists
      if (currentChapterId && currentChapterContent.length > 0) {
        chapters[currentChapterId].content = [...currentChapterContent];
      }

      // Start new chapter
      const id = crypto.randomUUID();
      const title = getNodeText(node);

      chapters[id] = {
        id,
        title,
        content: [node], // Start with the H1 heading
        order: order++,
        subChapterIds: [], // Could be enhanced to parse H2s
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      chapterOrder.push(id);
      currentChapterId = id;
      currentChapterContent = [node];
    } else if (currentChapterId) {
      // Add content to current chapter
      currentChapterContent.push(node);
    } else {
      // Content before first H1 - create a default chapter
      if (chapterOrder.length === 0) {
        const id = crypto.randomUUID();
        chapters[id] = {
          id,
          title: "Introduction",
          content: [node],
          order: order++,
          subChapterIds: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        chapterOrder.push(id);
        currentChapterId = id;
        currentChapterContent = [node];
      }
    }
  });

  // Save last chapter
  if (currentChapterId && currentChapterContent.length > 0) {
    chapters[currentChapterId].content = [...currentChapterContent];
  }

  return { chapters, subChapters: {}, chapterOrder, lastChangeTimestamp: 0 };
};

/**
 * Convert normalized structure → flat Slate format for saving
 * This ensures backward compatibility with the existing save format
 */
export const serializeToSlate = (state: ManualState): Descendant[] => {
  return state.chapterOrder.flatMap((chapterId) => {
    const chapter = state.chapters[chapterId];
    if (!chapter) return [];
    return chapter.content;
  });
};

/**
 * Create a new empty chapter with a given title
 */
export const createNewChapter = (
  title: string,
  order: number,
): NormalizedChapter => {
  const id = crypto.randomUUID();

  return {
    id,
    title,
    content: [
      {
        type: "h1",
        children: [{ text: title }],
      } as any,
      {
        type: "paragraph",
        children: [{ text: "" }],
      } as any,
    ],
    order,
    subChapterIds: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
};
